import { type Asset, primaryPhoto } from '../models/asset.model';
import type { CatalogItem, Order } from '../models/catalog.model';
import type { Collaborator, Department } from '../models/collaborator.model';
import type { EquipmentPack, OnboardingFlow } from '../models/onboarding.model';

/**
 * Jeu de données de la maquette v5 : mêmes numéros de série, mêmes dates, mêmes journaux d'audit.
 * Il sert à faire tourner l'application en local tant que le backend n'a pas de base — `pin-dev`
 * n'est pas provisionnée et l'authentification UniPortail n'est pas branchée.
 *
 * Deux écarts assumés par rapport à la maquette, imposés par les specs :
 * chaque matériel porte un **numéro d'inventaire** `PIN-AAAA-NNNNN` (RM-PIN-120 en fait le seul
 * identifiant obligatoire, la maquette ne l'affichait pas) ; et les **catégories** sont celles du
 * référentiel PJ-06 de SPEC-PIN-001 (« PC portable » et non « Ordinateur portable »,
 * « Clavier haut de gamme » et non « Clavier »), la maquette datant d'avant cette annexe.
 *
 * Date de référence figée par la maquette : 24.07.2026. Les calculs de garantie s'y rapportent,
 * sinon les badges « J−n » changeraient à chaque exécution.
 */
export const DEMO_TODAY = new Date(2026, 6, 24);

export const DEMO_ASSETS: readonly Asset[] = [
  {
    id: 'demo-01', inventoryNumber: 'PIN-2025-00002', serialNumber: 'C02XK1ANQ6L4', brand: 'Apple',
    name: 'MacBook Pro 14" M3', category: 'PC portable', status: 'Assigné',
    holder: 'Camille Roussel', holderId: null, holderType: 'collab', location: 'Siège',
    purchaseDate: '12.03.2025', warrantyMonths: 24, warrantyEnd: '12.03.2027', value: 2390,
    supplier: 'LDLC Pro', assignedOn: '15.03.2025', notes: null,
    photos: [],
    history: [
      { date: '12.03.2025', label: 'Fiche créée (réception commande CMD-2025-012)' },
      { date: '15.03.2025', label: 'Affecté à Camille Roussel — décharge PDF générée' },
    ],
  },
  {
    id: 'demo-02', inventoryNumber: 'PIN-2024-00005', serialNumber: 'DL5440-88214', brand: 'Dell',
    name: 'Latitude 5440', category: 'PC portable', status: 'Assigné',
    holder: 'Julien Mercier', holderId: null, holderType: 'collab', location: 'Télétravail',
    purchaseDate: '20.09.2024', warrantyMonths: 24, warrantyEnd: '20.09.2026', value: 1150,
    supplier: 'Dell Direct', assignedOn: '22.09.2024', notes: null,
    photos: [],
    history: [
      { date: '20.09.2024', label: 'Fiche créée (import CSV parc initial)' },
      { date: '22.09.2024', label: 'Affecté à Julien Mercier' },
    ],
  },
  {
    id: 'demo-03', inventoryNumber: 'PIN-2024-00006', serialNumber: 'DL5440-88215', brand: 'Dell',
    name: 'Latitude 5440', category: 'PC portable', status: 'Disponible',
    holder: null, holderId: null, holderType: null, location: 'Siège',
    purchaseDate: '20.09.2024', warrantyMonths: 30, warrantyEnd: '20.03.2027', value: 1150,
    supplier: 'Dell Direct', assignedOn: null, notes: null,
    photos: [],
    history: [{ date: '20.09.2024', label: 'Fiche créée (import CSV parc initial)' }],
  },
  {
    id: 'demo-04', inventoryNumber: 'PIN-2024-00001', serialNumber: 'MBA13-M2-4471', brand: 'Apple',
    name: 'MacBook Air 13" M2', category: 'PC portable', status: 'Disponible - À vérifier',
    holder: null, holderId: null, holderType: null, location: 'Siège',
    purchaseDate: '05.01.2024', warrantyMonths: 36, warrantyEnd: '05.01.2027', value: 1499,
    supplier: 'LDLC Pro', assignedOn: null, notes: null,
    photos: [],
    history: [
      { date: '05.01.2024', label: 'Fiche créée' },
      { date: '10.01.2024', label: 'Affecté à Marc Dupuis' },
      { date: '22.07.2026', label: 'Restitué (offboarding OFF-2026-003) → Disponible - À vérifier' },
    ],
  },
  {
    id: 'demo-05', inventoryNumber: 'PIN-2024-00007', serialNumber: 'IP15-KX22981', brand: 'Apple',
    name: 'iPhone 15', category: 'Smartphone', status: 'Assigné',
    holder: 'Sophie Lambert', holderId: null, holderType: 'collab', location: 'Siège',
    purchaseDate: '10.10.2024', warrantyMonths: 24, warrantyEnd: '10.10.2026', value: 969,
    supplier: 'Orange Pro', assignedOn: '11.10.2024', notes: null,
    photos: [],
    history: [
      { date: '10.10.2024', label: 'Fiche créée' },
      { date: '11.10.2024', label: 'Affecté à Sophie Lambert' },
    ],
  },
  {
    id: 'demo-06', inventoryNumber: 'PIN-2024-00003', serialNumber: 'SGS23-77120', brand: 'Samsung',
    name: 'Galaxy S23', category: 'Smartphone', status: 'En réparation',
    holder: 'Thomas Girard', holderId: null, holderType: 'collab', location: 'Siège',
    purchaseDate: '02.06.2024', warrantyMonths: 24, warrantyEnd: '02.06.2026', value: 859,
    supplier: 'Orange Pro', assignedOn: '03.06.2024', notes: null,
    photos: [],
    history: [
      { date: '02.06.2024', label: 'Fiche créée' },
      { date: '03.06.2024', label: 'Affecté à Thomas Girard' },
      { date: '21.07.2026', label: 'Envoyé en réparation (ticket TCK-116) — bon de retour BR-2026-031' },
    ],
  },
  {
    id: 'demo-07', inventoryNumber: 'PIN-2025-00003', serialNumber: 'DU27-30281', brand: 'Dell',
    name: 'Écran U2723QE 27"', category: 'Écran', status: 'Assigné',
    holder: 'Camille Roussel', holderId: null, holderType: 'collab', location: 'Siège',
    purchaseDate: '12.03.2025', warrantyMonths: 36, warrantyEnd: '12.03.2028', value: 649,
    supplier: 'LDLC Pro', assignedOn: '15.03.2025', notes: null,
    photos: [],
    history: [
      { date: '12.03.2025', label: 'Fiche créée' },
      { date: '15.03.2025', label: 'Affecté à Camille Roussel' },
    ],
  },
  {
    id: 'demo-08', inventoryNumber: 'PIN-2025-00004', serialNumber: 'DU27-30282', brand: 'Dell',
    name: 'Écran U2723QE 27"', category: 'Écran', status: 'Disponible',
    holder: null, holderId: null, holderType: null, location: 'Siège',
    purchaseDate: '12.03.2025', warrantyMonths: 36, warrantyEnd: '12.03.2028', value: 649,
    supplier: 'LDLC Pro', assignedOn: null, notes: null,
    photos: [],
    history: [{ date: '12.03.2025', label: 'Fiche créée' }],
  },
  {
    // Matériel attendu : le numéro de série est saisi à la réception (RM-PIN-116), pas à la commande.
    id: 'demo-09', inventoryNumber: 'PIN-2026-00002', serialNumber: null, brand: 'Lenovo',
    name: 'ThinkPad T14 Gen 5', category: 'PC portable', status: 'En commande',
    holder: null, holderId: null, holderType: null, location: 'Siège',
    purchaseDate: '—', warrantyMonths: null, warrantyEnd: null, value: 1290,
    supplier: 'Lenovo Pro', assignedOn: null, notes: null,
    photos: [],
    history: [
      { date: '18.07.2026', label: 'Asset attendu créé (commande CMD-2026-040 passée au fournisseur)' },
    ],
  },
  {
    id: 'demo-10', inventoryNumber: 'PIN-2025-00001', serialNumber: 'MXK-S3-99105', brand: 'Logitech',
    name: 'MX Keys S', category: 'Clavier haut de gamme', status: 'Assigné',
    holder: 'Nadia Benali', holderId: null, holderType: 'collab', location: 'Siège',
    purchaseDate: '14.02.2025', warrantyMonths: 24, warrantyEnd: '14.02.2027', value: 119,
    supplier: 'LDLC Pro', assignedOn: '14.02.2025', notes: null,
    photos: [],
    history: [
      { date: '14.02.2025', label: 'Fiche créée' },
      { date: '14.02.2025', label: 'Affecté à Nadia Benali' },
    ],
  },
  {
    id: 'demo-11', inventoryNumber: 'PIN-2024-00002', serialNumber: 'IPAD-A5-33067', brand: 'Apple',
    name: 'iPad Air 11"', category: 'Tablette', status: 'Perdu/Volé',
    holder: null, holderId: null, holderType: null, location: 'Télétravail',
    purchaseDate: '09.04.2024', warrantyMonths: 24, warrantyEnd: '09.04.2026', value: 719,
    supplier: 'LDLC Pro', assignedOn: null, notes: null,
    photos: [],
    history: [
      { date: '09.04.2024', label: 'Fiche créée' },
      { date: '02.05.2024', label: 'Affecté à Élodie Vasseur' },
      { date: '30.06.2026', label: 'Déclaré perdu (ticket TCK-109) → Perdu/Volé' },
    ],
  },
  {
    id: 'demo-12', inventoryNumber: 'PIN-2022-00001', serialNumber: 'HP840-G8-1204', brand: 'HP',
    name: 'EliteBook 840 G8', category: 'PC portable', status: 'Hors service',
    holder: null, holderId: null, holderType: null, location: 'Siège',
    purchaseDate: '01.05.2022', warrantyMonths: 36, warrantyEnd: '01.05.2025', value: 0,
    supplier: 'HP Store', assignedOn: null, notes: null,
    photos: [],
    history: [
      { date: '01.05.2022', label: 'Fiche créée' },
      { date: '12.06.2026', label: 'Réformé — carte mère HS, non réparable (ticket TCK-102)' },
    ],
  },
  {
    id: 'demo-13', inventoryNumber: 'PIN-2024-00004', serialNumber: 'C02WL0XYT9A2', brand: 'Apple',
    name: 'MacBook Pro 14" M2', category: 'PC portable', status: 'Assigné',
    holder: 'Thomas Girard', holderId: null, holderType: 'collab', location: 'Bureau secondaire',
    purchaseDate: '15.08.2024', warrantyMonths: 24, warrantyEnd: '15.08.2026', value: 2190,
    supplier: 'LDLC Pro', assignedOn: '16.08.2024', notes: null,
    photos: [],
    history: [
      { date: '15.08.2024', label: 'Fiche créée' },
      { date: '16.08.2024', label: 'Affecté à Thomas Girard' },
    ],
  },
  {
    id: 'demo-14', inventoryNumber: 'PIN-2024-00008', serialNumber: 'LG27-UL550-08', brand: 'LG',
    name: 'Écran 27UL550', category: 'Écran', status: 'Assigné',
    holder: 'Salle de réunion A', holderId: null, holderType: 'lieu', location: 'Siège',
    purchaseDate: '03.11.2024', warrantyMonths: 36, warrantyEnd: '03.11.2027', value: 329,
    supplier: 'LDLC Pro', assignedOn: '03.11.2024', notes: null,
    photos: [],
    history: [
      { date: '03.11.2024', label: 'Fiche créée' },
      { date: '03.11.2024', label: 'Affecté au lieu partagé « Salle de réunion A » (RM-PIN-018)' },
    ],
  },
  {
    id: 'demo-15', inventoryNumber: 'PIN-2026-00001', serialNumber: 'MTP-BLK-77812', brand: 'Apple',
    name: 'Magic Trackpad noir', category: 'Souris / touchpad haut de gamme', status: 'Disponible',
    holder: null, holderId: null, holderType: null, location: 'Siège',
    purchaseDate: '10.01.2026', warrantyMonths: 24, warrantyEnd: '10.01.2028', value: 155,
    supplier: 'LDLC Pro', assignedOn: null, notes: null,
    photos: [],
    history: [{ date: '10.01.2026', label: 'Fiche créée (réception commande CMD-2026-002)' }],
  },
];

/**
 * Référentiel des services. Il n'est décrit par aucune spec (hypothèse H-1 du plan) mais rien ne
 * fonctionne sans lui : un collaborateur doit être rattaché à un service **existant** (RM-PIN-191).
 *
 * Le backend expose déjà `POST /api/directory/departments` mais **aucune query de liste** : le
 * formulaire de création ne peut donc pas encore résoudre un `departmentId` réel. Ces identifiants
 * sont des GUID de démonstration, stables d'une exécution à l'autre pour que les fiches saisies
 * restent cohérentes tant que `GET /api/directory/departments` n'existe pas.
 */
export const DEMO_DEPARTMENTS: readonly Department[] = [
  { id: '01920000-0000-7000-8000-00000000d001', name: 'Ventes' },
  { id: '01920000-0000-7000-8000-00000000d002', name: 'Tech' },
  { id: '01920000-0000-7000-8000-00000000d003', name: 'RH' },
  { id: '01920000-0000-7000-8000-00000000d004', name: 'Comptabilité' },
  { id: '01920000-0000-7000-8000-00000000d005', name: 'Support' },
  { id: '01920000-0000-7000-8000-00000000d006', name: 'Marketing' },
  { id: '01920000-0000-7000-8000-00000000d007', name: 'Direction' },
];

/** Rattachement du jeu de démonstration par nom de service, pour rester lisible. */
function demoDepartmentId(name: string): string {
  return DEMO_DEPARTMENTS.find((department) => department.name === name)!.id;
}

export const DEMO_COLLABORATORS: readonly Collaborator[] = [
  {
    id: '01920000-0000-7000-8000-00000000c001', lastName: 'Roussel', firstName: 'Camille',
    email: 'camille.roussel@unipros.coop', departmentId: demoDepartmentId('Ventes'),
    jobTitle: 'Commerciale', employmentStatus: 'Actif', arrivalDate: '2025-03-10',
    managerId: null, internalNotes: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000c002', lastName: 'Mercier', firstName: 'Julien',
    email: 'julien.mercier@unipros.coop', departmentId: demoDepartmentId('Tech'),
    jobTitle: 'Développeur', employmentStatus: 'Actif', arrivalDate: '2024-09-16',
    managerId: null, internalNotes: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000c003', lastName: 'Lambert', firstName: 'Sophie',
    email: 'sophie.lambert@unipros.coop', departmentId: demoDepartmentId('RH'),
    jobTitle: 'DRH', employmentStatus: 'Actif', arrivalDate: '2023-01-09',
    managerId: null, internalNotes: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000c004', lastName: 'Benali', firstName: 'Nadia',
    email: 'nadia.benali@unipros.coop', departmentId: demoDepartmentId('Comptabilité'),
    jobTitle: 'Comptable', employmentStatus: 'Actif', arrivalDate: '2024-02-12',
    managerId: null, internalNotes: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000c005', lastName: 'Girard', firstName: 'Thomas',
    email: 'thomas.girard@unipros.coop', departmentId: demoDepartmentId('Support'),
    jobTitle: 'Support IT', employmentStatus: 'Actif', arrivalDate: '2024-05-27',
    managerId: null, internalNotes: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000c006', lastName: 'Vasseur', firstName: 'Élodie',
    email: 'elodie.vasseur@unipros.coop', departmentId: demoDepartmentId('Marketing'),
    jobTitle: 'Resp. Marketing', employmentStatus: 'Actif', arrivalDate: '2024-04-02',
    managerId: null, internalNotes: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000c007', lastName: 'Fontaine', firstName: 'Léa',
    email: 'lea.fontaine@unipros.coop', departmentId: demoDepartmentId('Ventes'),
    jobTitle: 'Commerciale', employmentStatus: 'À venir', arrivalDate: '2026-09-01',
    managerId: null, internalNotes: 'Arrivée prévue le 01.09.2026 — onboarding à préparer.',
  },
  {
    // Arrivée à quatre jours de la date de référence : c'est ce collaborateur qui produit un flux
    // « Échéance proche » au sens de RM-PIN-131.
    id: '01920000-0000-7000-8000-00000000c008', lastName: 'Cherif', firstName: 'Yanis',
    email: 'yanis.cherif@unipros.coop', departmentId: demoDepartmentId('Support'),
    jobTitle: 'Technicien support', employmentStatus: 'À venir', arrivalDate: '2026-07-28',
    managerId: '01920000-0000-7000-8000-00000000c005', internalNotes: null,
  },
];

/**
 * Packs d'équipements (SPEC-PIN-002, RM-PIN-038). Chacun porte au moins un type de matériel, et
 * distingue les lignes unitaires — qui donnent lieu à l'affectation d'une fiche matériel — des
 * consommables non sérialisés servis par sortie de stock (RM-PIN-133).
 *
 * Les types de matériel reprennent le référentiel PJ-06 de SPEC-PIN-001 pour les lignes unitaires.
 * Ils restent du texte libre : aucune capacité publiée ne permet aujourd'hui de valider un type
 * depuis ce module, l'inventaire ne l'ayant pas encore exposé.
 */
export const DEMO_EQUIPMENT_PACKS: readonly EquipmentPack[] = [
  {
    id: '01920000-0000-7000-8000-00000000p001',
    name: 'Pack Développeur',
    description: 'Poste de travail complet pour un profil technique.',
    state: 'Actif',
    lines: [
      { materialType: 'PC portable', quantity: 1, kind: 'unitaire' },
      { materialType: 'Écran', quantity: 2, kind: 'unitaire' },
      { materialType: "Station d'accueil", quantity: 1, kind: 'unitaire' },
      { materialType: 'Clavier haut de gamme', quantity: 1, kind: 'unitaire' },
      { materialType: 'Chargeur', quantity: 1, kind: 'consommable' },
      { materialType: 'Sacoche', quantity: 1, kind: 'consommable' },
    ],
  },
  {
    id: '01920000-0000-7000-8000-00000000p002',
    name: 'Pack Commercial',
    description: 'Mobilité : portable léger, smartphone et accessoires de déplacement.',
    state: 'Actif',
    lines: [
      { materialType: 'PC portable', quantity: 1, kind: 'unitaire' },
      { materialType: 'Smartphone', quantity: 1, kind: 'unitaire' },
      { materialType: 'Casque audio professionnel', quantity: 1, kind: 'unitaire' },
      { materialType: 'Chargeur', quantity: 2, kind: 'consommable' },
      { materialType: 'Adaptateur', quantity: 1, kind: 'consommable' },
    ],
  },
  {
    id: '01920000-0000-7000-8000-00000000p003',
    name: 'Pack Support',
    description: 'Technicien support : poste fixe et périphériques de diagnostic.',
    state: 'Actif',
    lines: [
      { materialType: 'PC portable', quantity: 1, kind: 'unitaire' },
      { materialType: 'Écran', quantity: 1, kind: 'unitaire' },
      { materialType: 'Smartphone', quantity: 1, kind: 'unitaire' },
      { materialType: 'Câble', quantity: 3, kind: 'consommable' },
    ],
  },
  {
    // Pack sorti de service : il reste lisible pour les flux passés, mais n'est plus assignable
    // (RM-PIN-023). C'est la seule sortie possible pour un pack déjà utilisé (RM-PIN-039).
    id: '01920000-0000-7000-8000-00000000p004',
    name: 'Pack Stagiaire 2025',
    description: 'Remplacé par le Pack Support en 2026.',
    state: 'Désactivé',
    lines: [
      { materialType: 'PC portable', quantity: 1, kind: 'unitaire' },
      { materialType: "Souris d'entrée de gamme", quantity: 1, kind: 'consommable' },
    ],
  },
];

/**
 * Flux d'onboarding (SPEC-PIN-002). Aucun offboarding : le use case n'est pas livré côté backend
 * (UC-09 à UC-12), et afficher des flux de départ fictifs laisserait croire le contraire.
 *
 * Yanis Cherif porte deux flux, dont un annulé : c'est la démonstration de RM-PIN-021, qui n'interdit
 * qu'un second flux **en cours** — un flux annulé ne bloque rien.
 */
export const DEMO_ONBOARDING_FLOWS: readonly OnboardingFlow[] = [
  {
    id: '01920000-0000-7000-8000-00000000f001',
    collaboratorId: '01920000-0000-7000-8000-00000000c007',
    type: 'Onboarding',
    packId: '01920000-0000-7000-8000-00000000p002',
    startedOn: '2026-07-20',
    keyDate: '2026-09-01',
    state: 'Dotation en cours',
    assignedTo: 'Thomas Girard',
    cancellationReason: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000f002',
    collaboratorId: '01920000-0000-7000-8000-00000000c008',
    type: 'Onboarding',
    packId: '01920000-0000-7000-8000-00000000p003',
    startedOn: '2026-07-22',
    keyDate: '2026-07-28',
    state: 'En attente de commande',
    assignedTo: 'Olivia Martin',
    cancellationReason: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000f003',
    collaboratorId: '01920000-0000-7000-8000-00000000c008',
    type: 'Onboarding',
    packId: '01920000-0000-7000-8000-00000000p004',
    startedOn: '2026-07-15',
    keyDate: '2026-07-28',
    state: 'Annulé',
    assignedTo: 'Olivia Martin',
    cancellationReason: 'Pack Stagiaire 2025 retiré du catalogue — flux repris avec le Pack Support.',
  },
  {
    id: '01920000-0000-7000-8000-00000000f004',
    collaboratorId: '01920000-0000-7000-8000-00000000c001',
    type: 'Onboarding',
    packId: '01920000-0000-7000-8000-00000000p001',
    startedOn: '2025-03-10',
    keyDate: '2025-03-10',
    state: 'Clôturé',
    assignedTo: 'Olivia Martin',
    cancellationReason: null,
  },
];

/**
 * Cibles d'une affectation partagée (RM-PIN-018, UC-14).
 *
 * ⚠️ Ce ne sont **pas** les localisations de RM-PIN-015, qui est une liste fermée de trois valeurs
 * (Siège, Bureau secondaire, Télétravail) désignant le site physique. Une salle de réunion n'y
 * figure pas : la spec confond deux notions sous le mot « lieu », et l'entité `Location` du Feature
 * Brief ne peut représenter que la première. Écart à faire trancher.
 */
export const DEMO_SHARED_HOLDERS: readonly { name: string; note: string }[] = [
  { name: 'Salle de réunion A', note: 'Siège — matériel partagé' },
  { name: 'Salle de réunion B', note: 'Siège — matériel partagé' },
  { name: 'Service Support', note: 'Matériel de service' },
];

/** Localisations physiques — liste fermée de RM-PIN-015. */
export const LOCATIONS: readonly string[] = ['Siège', 'Bureau secondaire', 'Télétravail'];

/** Équipe du Manager/RH de démonstration, qui borne son périmètre de visibilité (RM-PIN-195). */
export const DEMO_MANAGER_TEAM: readonly string[] = [
  'Camille Roussel',
  'Élodie Vasseur',
  'Léa Fontaine',
];

/** Photo produit quand la maquette en propose une, monogramme sinon. */
export function assetImage(name: string): string | null {
  if (name.includes('MacBook Pro')) return 'asset-macbook-pro.jpg';
  if (name.includes('MX Keys')) return 'asset-mx-keys.jpg';
  if (name.includes('Trackpad')) return 'asset-trackpad.avif';
  return null;
}

/**
 * Vignette d'un matériel : sa **photo principale** si la fiche en porte une (RM-PIN-122), sinon
 * l'image produit de la maquette, sinon `null` — l'appelant retombe alors sur le monogramme.
 */
export function assetThumbnail(asset: Asset): string | null {
  return primaryPhoto(asset)?.url ?? assetImage(asset.name);
}

/** Monogramme de vignette, repris de `monoOf()` dans la maquette. */
export function assetMonogram(name: string): string {
  const map: readonly [string, string][] = [
    ['MacBook Pro', 'MBP'], ['MacBook Air', 'MBA'], ['Latitude', 'DL54'], ['iPhone', 'IP15'],
    ['Galaxy', 'S23'], ['U2723', 'U27'], ['ThinkPad', 'T14'], ['MX Keys', 'MX'],
    ['MX Master', 'MX'], ['Trackpad', 'TPD'], ['iPad', 'IPD'], ['EliteBook', '840'],
    ['27UL', 'LG27'], ['Jabra', 'JBR'], ['Casque', 'CSQ'], ['Écran', 'ÉCR'], ['Smartphone', 'TEL'],
  ];
  for (const [needle, mono] of map) {
    if (name.includes(needle)) return mono;
  }
  return name.slice(0, 3).toUpperCase();
}

/** Fond et couleur de vignette par catégorie, repris de `tileOf()` et étendu au référentiel PJ-06. */
export function categoryTile(category: string): { bg: string; fg: string } {
  const tiles: Record<string, { bg: string; fg: string }> = {
    'PC portable': { bg: 'var(--surface-brand-subtle)', fg: 'var(--text-brand)' },
    'PC fixe': { bg: 'var(--surface-brand-subtle)', fg: 'var(--text-brand)' },
    Smartphone: { bg: 'var(--surface-info-subtle)', fg: 'var(--app-uni-it-text-brand)' },
    Tablette: { bg: 'var(--surface-info-subtle)', fg: 'var(--app-uni-it-text-brand)' },
    Téléphone: { bg: 'var(--surface-info-subtle)', fg: 'var(--app-uni-it-text-brand)' },
    Écran: { bg: 'var(--surface-success-subtle)', fg: 'var(--text-primary)' },
    "Station d'accueil": { bg: 'var(--surface-warning-subtle)', fg: 'var(--text-primary)' },
    'Casque audio professionnel': { bg: 'var(--surface-warning-subtle)', fg: 'var(--text-primary)' },
    'Clavier haut de gamme': { bg: 'var(--surface-warning-subtle)', fg: 'var(--text-primary)' },
    'Souris / touchpad haut de gamme': { bg: 'var(--surface-warning-subtle)', fg: 'var(--text-primary)' },
  };
  return tiles[category] ?? { bg: 'var(--surface-subtle)', fg: 'var(--text-secondary)' };
}

/**
 * Catalogue interne (SPEC-PIN-003). Chaque référence porte un mode d'acquisition obligatoire
 * (RM-PIN-040) et, en leasing, un loyer mensuel et une durée d'engagement (RM-PIN-141).
 *
 * `inventoryCategory` est le rattachement à l'inventaire qui rend la disponibilité calculable
 * (RM-PIN-140). Il est volontairement absent sur une référence, pour que le cas « Inconnue » — non
 * bloquant — soit visible à l'écran.
 */
export const DEMO_CATALOG_ITEMS: readonly CatalogItem[] = [
  {
    id: '01920000-0000-7000-8000-00000000i001', reference: 'CAT-PC-001',
    label: 'MacBook Pro 14" M3', category: 'PC portable', mode: 'achat',
    unitPrice: 2390, monthlyRent: null, leaseDurationMonths: null,
    isActive: true, isConsumable: false, inventoryCategory: 'PC portable',
  },
  {
    id: '01920000-0000-7000-8000-00000000i002', reference: 'CAT-PC-002',
    label: 'Dell Latitude 5450', category: 'PC portable', mode: 'achat',
    unitPrice: 1190, monthlyRent: null, leaseDurationMonths: null,
    isActive: true, isConsumable: false, inventoryCategory: 'PC portable',
  },
  {
    // Leasing : le montant confronté au seuil est loyer × durée, soit 1 656 € (RM-PIN-141).
    id: '01920000-0000-7000-8000-00000000i003', reference: 'CAT-PC-003',
    label: 'Lenovo ThinkPad T14 Gen 5 (leasing)', category: 'PC portable', mode: 'leasing',
    unitPrice: null, monthlyRent: 46, leaseDurationMonths: 36,
    isActive: true, isConsumable: false, inventoryCategory: 'PC portable',
  },
  {
    id: '01920000-0000-7000-8000-00000000i004', reference: 'CAT-ECR-001',
    label: 'Écran Dell U2723QE 27"', category: 'Écran', mode: 'achat',
    unitPrice: 649, monthlyRent: null, leaseDurationMonths: null,
    isActive: true, isConsumable: false, inventoryCategory: 'Écran',
  },
  {
    id: '01920000-0000-7000-8000-00000000i005', reference: 'CAT-TEL-001',
    label: 'iPhone 15', category: 'Smartphone', mode: 'achat',
    unitPrice: 969, monthlyRent: null, leaseDurationMonths: null,
    isActive: true, isConsumable: false, inventoryCategory: 'Smartphone',
  },
  {
    id: '01920000-0000-7000-8000-00000000i006', reference: 'CAT-DOC-001',
    label: "Station d'accueil Dell WD19S", category: "Station d'accueil", mode: 'achat',
    unitPrice: 229, monthlyRent: null, leaseDurationMonths: null,
    isActive: true, isConsumable: false, inventoryCategory: "Station d'accueil",
  },
  {
    // RM-PIN-144 — consommable : commandable par quantité, aucun numéro de série à la réception.
    id: '01920000-0000-7000-8000-00000000i007', reference: 'CAT-CON-001',
    label: 'Chargeur USB-C 96 W', category: 'Chargeur', mode: 'achat',
    unitPrice: 69, monthlyRent: null, leaseDurationMonths: null,
    isActive: true, isConsumable: true, inventoryCategory: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000i008', reference: 'CAT-CON-002',
    label: 'Sacoche 14"', category: 'Sacoche', mode: 'achat',
    unitPrice: 45, monthlyRent: null, leaseDurationMonths: null,
    isActive: true, isConsumable: true, inventoryCategory: null,
  },
  {
    // Référence sortie du catalogue : RM-PIN-043 interdit de l'ajouter à un panier.
    id: '01920000-0000-7000-8000-00000000i009', reference: 'CAT-PC-004',
    label: 'HP EliteBook 840 G8 (fin de vie)', category: 'PC portable', mode: 'achat',
    unitPrice: 1090, monthlyRent: null, leaseDurationMonths: null,
    isActive: false, isConsumable: false, inventoryCategory: 'PC portable',
  },
];

/**
 * Commandes de démonstration, une par étape du cycle de vie pour que chaque transition de
 * RM-PIN-053 et chaque écran de §6 soit atteignable.
 *
 * CMD-2026-041 est créée par Thomas Girard : l'approuver depuis son propre compte est refusé par
 * RM-PIN-049 (séparation des tâches), ce qui rend la règle démontrable en changeant de rôle.
 */
export const DEMO_ORDERS: readonly Order[] = [
  {
    id: '01920000-0000-7000-8000-00000000o001', orderNumber: 'CMD-2026-041',
    status: "En attente d'approbation",
    lines: [
      {
        catalogItemId: '01920000-0000-7000-8000-00000000i001', reference: 'CAT-PC-001',
        label: 'MacBook Pro 14" M3', mode: 'achat', isConsumable: false,
        quantity: 2, unitAmount: 2390, leaseEndDate: null, receivedQuantity: 0,
      },
    ],
    totalAmount: 4780, createdBy: 'Thomas Girard', createdOn: '2026-07-23',
    approvedBy: null, refusalReason: null, beneficiary: null, purchaseRequestNumber: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000o002', orderNumber: 'CMD-2026-040',
    status: 'Expédiée',
    lines: [
      {
        catalogItemId: '01920000-0000-7000-8000-00000000i003', reference: 'CAT-PC-003',
        label: 'Lenovo ThinkPad T14 Gen 5 (leasing)', mode: 'leasing', isConsumable: false,
        quantity: 1, unitAmount: 1656, leaseEndDate: '2029-07-18', receivedQuantity: 0,
      },
      {
        catalogItemId: '01920000-0000-7000-8000-00000000i007', reference: 'CAT-CON-001',
        label: 'Chargeur USB-C 96 W', mode: 'achat', isConsumable: true,
        quantity: 3, unitAmount: 69, leaseEndDate: null, receivedQuantity: 1,
      },
    ],
    totalAmount: 1863, createdBy: 'Thomas Girard', createdOn: '2026-07-18',
    approvedBy: 'Nadia Benali', refusalReason: null, beneficiary: 'Yanis Cherif',
    purchaseRequestNumber: 'DA-2026-017',
  },
  {
    id: '01920000-0000-7000-8000-00000000o003', orderNumber: 'CMD-2026-039',
    status: 'Approuvée',
    lines: [
      {
        catalogItemId: '01920000-0000-7000-8000-00000000i004', reference: 'CAT-ECR-001',
        label: 'Écran Dell U2723QE 27"', mode: 'achat', isConsumable: false,
        quantity: 2, unitAmount: 649, leaseEndDate: null, receivedQuantity: 0,
      },
    ],
    totalAmount: 1298, createdBy: 'Olivia Martin', createdOn: '2026-07-15',
    approvedBy: 'Nadia Benali', refusalReason: null, beneficiary: null, purchaseRequestNumber: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000o004', orderNumber: 'CMD-2026-036',
    status: 'Livrée',
    lines: [
      {
        catalogItemId: '01920000-0000-7000-8000-00000000i006', reference: 'CAT-DOC-001',
        label: "Station d'accueil Dell WD19S", mode: 'achat', isConsumable: false,
        quantity: 1, unitAmount: 229, leaseEndDate: null, receivedQuantity: 1,
      },
    ],
    totalAmount: 229, createdBy: 'Thomas Girard', createdOn: '2026-06-30',
    approvedBy: null, refusalReason: null, beneficiary: null, purchaseRequestNumber: null,
  },
  {
    id: '01920000-0000-7000-8000-00000000o005', orderNumber: 'CMD-2026-034',
    status: 'Refusée',
    lines: [
      {
        catalogItemId: '01920000-0000-7000-8000-00000000i005', reference: 'CAT-TEL-001',
        label: 'iPhone 15', mode: 'achat', isConsumable: false,
        quantity: 4, unitAmount: 969, leaseEndDate: null, receivedQuantity: 0,
      },
    ],
    totalAmount: 3876, createdBy: 'Thomas Girard', createdOn: '2026-06-24',
    approvedBy: null,
    refusalReason:
      'Budget mobilité déjà consommé sur le trimestre : à représenter au T4 avec la justification '
      + 'des renouvellements.',
    beneficiary: null, purchaseRequestNumber: null,
  },
];
